import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import SetupPracticeWizard from '../components/setup-practice/SetupPracticeWizard';
import SetupOfficeStaffWizard from '../components/setup-practice/SetupOfficeStaffWizard';

export default function SetupPractice() {
  const { user, tokens } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [practiceRequest, setPracticeRequest] = useState(null);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [changeRoleDialogOpen, setChangeRoleDialogOpen] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';

  useEffect(() => {
    const fetchPracticeRequest = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`${apiBase}/users/${user.id}/setup-practice-request`, {
          headers: {
            'Authorization': `Bearer ${tokens.id_token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch practice request');
        }

        const data = await response.json();
        setPracticeRequest(data);
      } catch (error) {
        console.error('Error fetching practice request:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPracticeRequest();
  }, [user, tokens, apiBase]);

  if (!user) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Typography color="error" variant="h6">
          Error: {error}
        </Typography>
      </Container>
    );
  }

  if (practiceRequest?.status === 'Submitted') {
    return (
      <Container maxWidth="md" disableGutters sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Practice setup request submitted
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              Your practice setup request has been submitted and is currently under review. 
              We will notify you once your request has been processed.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Provider information
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1">
                  <strong>Name:</strong> {practiceRequest.provider_name_prefix} {practiceRequest.provider_first_name} {practiceRequest.provider_middle_name} {practiceRequest.provider_last_name} {practiceRequest.provider_name_suffix}
                </Typography>
                <Typography variant="body1">
                  <strong>NPI:</strong> {practiceRequest.provider_npi}
                </Typography>
                <Typography variant="body1">
                  <strong>Credential:</strong> {practiceRequest.provider_credential}
                </Typography>
                {/* Provider address no longer collected */}
                <Typography variant="body1">
                  <strong>Phone:</strong> {practiceRequest.provider_phone}
                </Typography>
                <Typography variant="body1">
                  <strong>Fax:</strong> {practiceRequest.provider_fax}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Practice information
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1">
                  <strong>Practice Name:</strong> {practiceRequest.practice_name}
                </Typography>
                <Typography variant="body1">
                  <strong>NPI:</strong> {practiceRequest.practice_npi}
                </Typography>
                {/* Billing Address */}
                <Typography variant="body1">
                  <strong>Billing Address:</strong> {practiceRequest.billing_street_1} {practiceRequest.billing_street_2}
                </Typography>
                <Typography variant="body1">
                  <strong>City, State, ZIP:</strong> {practiceRequest.billing_city}, {practiceRequest.billing_state} {practiceRequest.billing_zip_code}
                </Typography>
                {/* Shipping Address */}
                <Typography variant="body1">
                  <strong>Shipping Address:</strong> {practiceRequest.shipping_street_1} {practiceRequest.shipping_street_2}
                </Typography>
                <Typography variant="body1">
                  <strong>City, State, ZIP:</strong> {practiceRequest.shipping_city}, {practiceRequest.shipping_state} {practiceRequest.shipping_zip_code}
                </Typography>
                <Typography variant="body1">
                  <strong>Phone:</strong> {practiceRequest.practice_phone}
                </Typography>
                <Typography variant="body1">
                  <strong>Fax:</strong> {practiceRequest.practice_fax}
                </Typography>
                <Typography variant="body1">
                  <strong>Email:</strong> {practiceRequest.practice_email}
                </Typography>
                <Typography variant="body1">
                  <strong>Website:</strong> {practiceRequest.practice_website}
                </Typography>
                {/* Additional practice details */}
                {practiceRequest.practice_dba && (
                  <Typography variant="body1">
                    <strong>DBA:</strong> {practiceRequest.practice_dba}
                  </Typography>
                )}
                {practiceRequest.practice_ein && (
                  <Typography variant="body1">
                    <strong>EIN:</strong> {practiceRequest.practice_ein}
                  </Typography>
                )}
                <Typography variant="body1">
                  <strong>Tax Exemption Requested:</strong> {practiceRequest.practice_request_tax_exemption ? 'Yes' : 'No'}
                </Typography>
                {practiceRequest.practice_state_tax_id && (
                  <Typography variant="body1">
                    <strong>State Tax ID:</strong> {practiceRequest.practice_state_tax_id}
                  </Typography>
                )}
                {practiceRequest.practice_type && (
                  <Typography variant="body1">
                    <strong>Practice Type:</strong> {practiceRequest.practice_type}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* On-site collections summary */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                On-site collections
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body1">
                  <strong>Planning to perform on-site collections:</strong> {practiceRequest.practice_is_doing_in_house_collection ? 'Yes' : 'No'}
                </Typography>
                {practiceRequest.practice_is_doing_in_house_collection && (
                  <Typography variant="body1">
                    <strong>IFU agreement:</strong> {practiceRequest.agreement_key_for_in_house_collection === 'ifu' ? 'Agreed' : 'Not agreed'}
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              variant="outlined"
              color="error"
              onClick={() => setUndoDialogOpen(true)}
            >
              Undo Submit
            </Button>

            <Dialog
              open={undoDialogOpen}
              onClose={() => setUndoDialogOpen(false)}
            >
              <DialogTitle>Undo submission</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to undo the submission of your practice setup request? 
                  This will allow you to make changes to your request before submitting it again.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setUndoDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={async () => {
                    try {
                      const response = await fetch(`${apiBase}/users/${user.id}/setup-practice-request`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${tokens.id_token}`
                        },
                        body: JSON.stringify({
                          ...practiceRequest,
                          status: 'Unsubmitted'
                        })
                      });

                      if (!response.ok) {
                        throw new Error('Failed to undo submission');
                      }

                      const updatedRequest = await response.json();
                      setPracticeRequest(updatedRequest);
                      setUndoDialogOpen(false);
                    } catch (error) {
                      console.error('Error undoing submission:', error);
                      setError(error.message);
                    }
                  }}
                  color="error"
                >
                  Undo Submit
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Office staff follow a simplified two-step wizard
  if (user.role === 'PendingOfficeStaff') {
    const targetRole = 'PendingProvider';
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Card
            variant="outlined"
            onClick={() => setChangeRoleDialogOpen(true)}
            sx={{ cursor: 'pointer', display: 'inline-block', borderColor: '#000000' }}
          >
            <CardContent>
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#000000', textDecoration: 'underline' }}
              >
                I'm not Office Staff, I'm a Provider.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom>
          Office staff: set up your practice
        </Typography>
        <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
          Please enter your practice NPI and confirm practice details to complete setup.
        </Typography>
        <SetupOfficeStaffWizard
          practiceRequest={practiceRequest}
          onUpdate={setPracticeRequest}
        />

        <Dialog open={changeRoleDialogOpen} onClose={() => setChangeRoleDialogOpen(false)}>
          <DialogTitle>Switch role?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              If you selected the wrong role, you can correct it here. Click Confirm to change your role to Provider.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangeRoleDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={changingRole}
              onClick={async () => {
                try {
                  setChangingRole(true);
                  const res = await fetch(`${apiBase}/users/${user.id}/role`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${tokens.id_token}`,
                    },
                    body: JSON.stringify({ role: targetRole }),
                  });
                  if (!res.ok) {
                    throw new Error('Failed to update role');
                  }
                  window.location.reload();
                } catch (e) {
                  console.error(e);
                  setError(e.message);
                } finally {
                  setChangingRole(false);
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Card
          variant="outlined"
          onClick={() => setChangeRoleDialogOpen(true)}
          sx={{ cursor: 'pointer', display: 'inline-block', borderColor: '#000000' }}
        >
          <CardContent>
            <Typography
              variant="body2"
              sx={{ fontStyle: 'italic', color: '#000000', textDecoration: 'underline' }}
            >
              I'm not a Provider, I'm Office Staff.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Typography variant="h4" component="h1" gutterBottom>
        Set up your practice
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
        Please provide your practice information to complete your provider setup.
      </Typography>
      <SetupPracticeWizard
        practiceRequest={practiceRequest}
        onUpdate={setPracticeRequest}
      />

      <Dialog open={changeRoleDialogOpen} onClose={() => setChangeRoleDialogOpen(false)}>
        <DialogTitle>Switch role?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you selected the wrong role, you can correct it here. Click Confirm to change your role to Office Staff.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeRoleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={changingRole}
            onClick={async () => {
              const targetRole = 'PendingOfficeStaff';
              try {
                setChangingRole(true);
                const res = await fetch(`${apiBase}/users/${user.id}/role`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${tokens.id_token}`,
                  },
                  body: JSON.stringify({ role: targetRole }),
                });
                if (!res.ok) {
                  throw new Error('Failed to update role');
                }
                window.location.reload();
              } catch (e) {
                console.error(e);
                setError(e.message);
              } finally {
                setChangingRole(false);
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 
